import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDate } from './dates';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, EmptyRow, PageHeader, Pager, FilterBar, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { PAGE_SIZE, PLAT, PlatformBillingList, RunResult, keys, subStatusLabel, subStatusTone } from './api';
import { BillingTabs, Money } from './parts';

/** Platform: every customer with plan, status, next invoice and overdue balance. */
export function OverviewPage() {
  const list = useListState({ sort: 'tenantName' });
  const canManage = useCan('platform.billing_manage');
  const qc = useQueryClient();
  const toast = useToast();
  const query = { page: list.page, pageSize: PAGE_SIZE, search: list.search, sort: list.sort, order: list.order };
  const q = useQuery({
    queryKey: keys.overview(query),
    queryFn: () => api.get(`${PLAT}/tenants`, { query, schema: PlatformBillingList }),
    placeholderData: keepPreviousData,
  });
  const run = useMutation({
    mutationFn: () => api.post(`${PLAT}/run`, { body: {}, schema: RunResult }),
    onSuccess: async (r) => {
      toast(t('billingUi.overview.runDone', { count: r.drafts }));
      await qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
  const sortProps = { sort: list.sort, order: list.order, onSort: list.toggleSort };
  return (
    <>
      <PageHeader
        title={t('billingUi.platform.title')}
        description={t('billingUi.platform.description')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={run.isPending} onClick={() => run.mutate()}>
              {t('billingUi.overview.run')}
            </Button>
          ) : null
        }
      />
      <BillingTabs />
      <ErrorLine error={run.error} />
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('common.search')} hint={t('billingUi.overview.searchHint')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('billingUi.overview.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('billingUi.col.customer')} field="tenantName" {...sortProps} />
                <SortHeader label={t('billingUi.col.plan')} field="planName" {...sortProps} />
                <Th>{t('common.status')}</Th>
                <Th>{t('billingUi.col.nextInvoice')}</Th>
                <SortHeader label={t('billingUi.col.overdue')} field="overdueBalance" {...sortProps} />
                <Th>{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={6} /> : null}
              {q.data.items.map((r) => (
                <tr key={r.tenantId} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{r.tenantName}</td>
                  <td className="px-4 py-3">{r.planName ?? t('billingUi.noPlan')}</td>
                  <td className="px-4 py-3">
                    {r.status ? <Badge tone={subStatusTone(r.status)}>{subStatusLabel(r.status)}</Badge> : <Badge>{t('billingUi.noSubscription')}</Badge>}
                    {r.restriction !== 'none' ? <div className="mt-1 text-xs text-slate-700">{t(`billingUi.restriction.${r.restriction}`)}</div> : null}
                  </td>
                  <td className="px-4 py-3">{r.nextInvoiceOn ? formatDate(r.nextInvoiceOn) : ''}</td>
                  <td className="px-4 py-3">
                    {r.overdueBalance > 0 ? (
                      <>
                        <Money pence={r.overdueBalance} />
                        <div className="text-xs text-slate-700">{t('billingUi.overdueDays', { count: r.oldestOverdueDays })}</div>
                      </>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-3">
                      <Link className="font-medium text-brand hover:underline" to={`/platform/billing/tenants/${r.tenantId}`} state={{ name: r.tenantName }} aria-label={t('billingUi.subscriptionFor', { name: r.tenantName })}>
                        {t('billingUi.col.subscription')}
                      </Link>
                      <Link className="font-medium text-brand hover:underline" to={`/platform/billing/invoices?tenantId=${r.tenantId}`} aria-label={t('billingUi.invoicesFor', { name: r.tenantName })}>
                        {t('billingUi.tabs.invoices')}
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
